import React from 'react'
import axios from 'axios/index'
import store from './store'
import { Redirect } from 'react-router-dom'
import globals from './globals'
import DataStore from './DataStore'
import { ACTION_LOGOUT } from './redux/actions'

const DataSource = {

    /*
        todo: Look into converting this into a class, and adding store.subscribe() into the constructor
        since I believe it works more consistently than using store.getState on its own, currently
        this object doesnt handle changes. Something like this:
        store.subscribe(() => {
            let currentToken = store.getState().auth.token

            if (this.token !== currentToken) {
                console.log('Some deep nested property changed from', this.token, 'to', currentToken);
                this.token = currentToken;
            }
        });
    */

    getConfig () {
        return {
            headers: new Headers({
                Authorization: `Bearer ${this.getAccessToken()}`
            })
        }
    },

    getData (url, onSuccess, onFailure = () => {}) {
        this.validateTimestamp()
        fetch(url, this.getConfig())
            .then(this.handleErrors)
            .then(result => {
                try {
                    return result.json()
                } catch (error) {
                    // This error occurs when this.handleErrors returns a <Redirect /> component as
                    // a response so result.json will not work. Leaving for now until I find a more
                    // elegant solution.
                }
            })
            .then(result => {
                onSuccess(result)
            })
            .catch(error => {
                globals.handleError(error).then(eventId => {
                    onFailure(eventId)
                })
            })
    },

    getBlob (url, onSuccess) {
        fetch(url, this.getConfig())
            .then(this.handleErrors)
            .then(result => {
                try {
                    return result.blob()
                } catch (error) {
                    // This error occurs when this.handleErrors returns a <Redirect /> component as
                    // a response so result.blob will not work. Leaving for now until I find a more
                    // elegant solution.
                }
            })
            .then(result => {
                onSuccess(result)
            })
            .catch(error => {
                globals.handleError(error)
            })
    },

    postData (url, data, onSuccess, onFailure) {
        const config = {
            headers: { Authorization: `Bearer ${this.getAccessToken()}` }
        }
        axios.post(url, data, config)
            .then(response => {
                onSuccess(response)
            })
            .catch(response => {
                if (response && (response.response && response.response.status !== 422 && response.response.status !== 401) || !response.response) {
                    globals.handleError(response.message)
                }

                onFailure(response)
            })
    },

    postMobileData (url, data = {}) {
        return new Promise((resolve, reject) => {
            axios({
                method: 'post',
                url: url,
                data: data,
                ...this.getMobileConfig()
            })
                .then(response => {
                    resolve(response)
                })
                .catch(response => {
                    this.handleErrors(url, response)
                    reject(response)
                })
        })
    },

    getMobileConfig () {
        const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        }

        if (DataStore.getItem('bearerToken')) {
            headers.Authorization = `Bearer ${DataStore.getItem('bearerToken')}`
        }

        return {
            headers: headers
        }
    },

    handleErrors (response) {
        if (!response.ok) {
            if (response.status === 401) {
                return <Redirect to='/' />
            } else {
                throw new Error(`HTTP status ${response.status} from request to URL ${response.url} - ${response.statusText}`)
            }
        }

        return response
    },

    getAccessToken () {
        return store.getState().auth.token
    },

    getExchangeAccessToken () {
        return store.getState().auth.exchangeAccessToken
    },

    validateTimestamp () {
        const loggedInAt = this.getDiffInMins(store.getState().auth.loggedInAt)
        // Log someone out automatically after 24 hours
        if (loggedInAt > 1440 && !store.getState().auth.persistLogin) {
            this.logout()
        }
    },

    getDiffInMins (timestamp) {
        const difference = Date.now() - timestamp
        return Math.round(difference / 1000 / 60)
    },

    logout () {
        store.dispatch({
            type: ACTION_LOGOUT,
            value: {}
        })
    },

    requestMobileToken (mobile) {
        return this.postMobileData(`${process.env.MIX_API_URL}v1/mobile-token/request`, { mobileNumber: mobile })
    },

    verifyMobileToken (mobile, token) {
        return this.postMobileData(`${process.env.MIX_API_URL}v1/mobile-token/verify`, {
            mobileNumber: mobile,
            token: token
        })
    },

    postSendMfaCode (action, onSuccess, onFailure) {
        return this.postData(`${process.env.MIX_API_URL}v1/create-mfa`, {action: action}, onSuccess, onFailure)
    },

    getCustomerMaskedEmailAndMobile (onSuccess, onFailure) {
        return this.getData(`${process.env.MIX_API_URL}v1/mfa`, onSuccess, onFailure)
    },

    getAvailableTechnicianDates (serviceId, startDate, onSuccess, onFailure) {
        return DataSource.postData(`${process.env.MIX_API_URL}v1/nbn-direct/appointments/${serviceId}/sync-reschedule`, {start_time: startDate}, onSuccess, onFailure)
    },

    getSimQrCode (serviceId, onSuccess, onFailure) {
        return DataSource.getData(`${process.env.MIX_API_URL}v1/mobile/${serviceId}/esim-qr-code-image`, onSuccess, onFailure)
    },
    postActivateMobileService(data, onSuccess, onFailure) {
        return DataSource.postData(`${process.env.MIX_API_URL}v1/mobile/${data.serviceId}/activate`, data, onSuccess, onFailure)
    },
    postActivateNewSim (data, onSuccess, onFailure) {
        return DataSource.postData(`${process.env.MIX_API_URL}v1/mobile/${data.hspaService}/sim-replace`, data, onSuccess, onFailure)
    },

    async postRescheduleAppointment (serviceId, slotId) {
        const config = {
            headers: {Authorization: `Bearer ${DataSource.getAccessToken()}`}
        }
        return await axios.patch(`${process.env.MIX_API_URL}v1/nbn-direct/appointments/${serviceId}/reschedule`, {slot_id: slotId}, config)
    }

}

export const patchOnceOffPayment = async ({nonce, braintreeNonce, updatePaymentMethod, amountCents}) => {
    try {
        return await axios.patch(`${process.env.MIX_EXPERIENCE_API_URL}/v1/payment-management/customer-payment-nonces/${nonce}`, {
            braintreeNonce,  
            updatePaymentMethod,
            amountCents,         
        })
    } catch (error) {
        return error
    }
}

export const getStandalonePaymentDetails = async (nonce) => {
    try {
        return await axios.get(`${process.env.MIX_EXPERIENCE_API_URL}/v1/payment-management/customer-payment-nonces/${nonce}`)
    } catch (error) {
        return error
    }
};

export const getXAPIHeader = (exchangeToken) => {
    const token = DataSource.getExchangeAccessToken() ? DataSource.getExchangeAccessToken(): exchangeToken
    return {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }
    }
}

export const postTokenExchange = async (contactEmail) => {
    try {
        return await axios.post(`${process.env.MIX_APP_TOKEN_EXCHANGE_API_URL}/v1/oauth/token`, {
            username:  contactEmail,
            token: DataSource.getAccessToken(),
            token_type: 'superhub_access_token',
            scope: 'access-superhub',
        })
    } catch (error) {
        return error
    }
}

export const getCustomerDetailsXAPI = async(customerId, exchangeAccessToken) => {
    try{
        return await axios.get(`${process.env.MIX_EXPERIENCE_API_URL}/v1/customer-management/customers/${customerId}?include=services`, getXAPIHeader(exchangeAccessToken))
    } catch (error) {
        return error
    }
    
}

export default DataSource



// WEBPACK FOOTER //
// ./resources/js/DataSource.js